import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Box,
  useBoolean,
  Divider,
  Icon,
  SimpleGrid,
  usePrevious,
} from "@chakra-ui/react";
import TraitTile from "components/TraitTile";
import useTraits from "hooks/useTraits";
import TraitsList from "components/TraitsList";
import useTranslate from "hooks/useTranslate";
import useAssets from "hooks/useAssets";
import UnstakePopup from "components/UnstakePopup";
import useBear from "hooks/useBear";
import useStakedTraits from "hooks/useBearStakedTraits";
import WardrobeIcon from "components/WardrobeIcon";
import StoreIcon from "components/StoreIcon";
import { Link } from "react-router-dom";
import TraitsChangedPopup from "components/TraitsChangedPopup";

const BearStakedTraitsContainer = () => {
  const translate = useTranslate();
  const { fancyBearsInWallet } = useAssets();
  const { tokenId, metadata, furColor } = useBear();
  const lastTokenId = usePrevious(tokenId);
  const { traitsByTokenId } = useTraits();
  const [stakedTraits, readStekedTraits] = useStakedTraits(tokenId);

  const [selectedTraits, setSelectedTraits] = useState<number[]>();
  const [isUnstakePopupOpen, setIsUnstakePopupOpen] = useBoolean();
  const [isUnstakeSuccessPopupOpen, setIsUnstakeSuccessPopupOpen] =
    useBoolean();
  const isMultiple = (stakedTraits?.length || 0) > 1;
  const isOwner = fancyBearsInWallet?.includes(tokenId!);

  const traitsAfterUnstake = useMemo(() => {
    if (selectedTraits?.length && metadata?.traits) {
      return metadata?.traits.map((tokenId, index) =>
        selectedTraits.includes(tokenId)
          ? metadata.originalTraits[index]
          : tokenId
      );
    }

    return [];
  }, [selectedTraits, metadata]);

  const handleUnstake = useCallback(
    (tokenId: number) => {
      setSelectedTraits([tokenId]);
      setIsUnstakePopupOpen.on();
    },
    [setIsUnstakePopupOpen]
  );

  const handleUnstakeAll = useCallback(() => {
    setSelectedTraits(stakedTraits);
    setIsUnstakePopupOpen.on();
  }, [setIsUnstakePopupOpen, stakedTraits]);

  const handleSuccess = () => {
    setIsUnstakePopupOpen.off();
    setIsUnstakeSuccessPopupOpen.on();
  };

  useEffect(() => {
    if (lastTokenId === undefined) {
      return;
    }
    if (tokenId !== lastTokenId) {
      readStekedTraits();
    }
  }, [tokenId, lastTokenId, readStekedTraits]);

  return (
    <Box w="full" bg="dark.500">
      <TraitsList title={translate("stakedTraits:title")} w="full" columns={1}>
        {stakedTraits?.map((tokenId) => {
          const trait = traitsByTokenId[tokenId];
          return trait ? (
            <TraitTile key={tokenId} tokenId={tokenId} name={trait.name}>
              {isOwner && (
                <Button
                  size="sm"
                  colorScheme="dark"
                  onClick={() => handleUnstake(tokenId)}
                  mr="1"
                >
                  {translate("common:unstake")}
                </Button>
              )}
            </TraitTile>
          ) : null;
        })}
      </TraitsList>

      {isOwner && (
        <Box p="4">
          {isMultiple && (
            <>
              <Button w="full" onClick={handleUnstakeAll}>
                {translate("stakedTraits:unstakeAll", {
                  count: stakedTraits?.length,
                })}
              </Button>

              <Divider borderColor="bright.500" my="3" w="full" />
            </>
          )}

          <SimpleGrid gap="3" columns={2}>
            <Link to={`/store/${tokenId}`}>
              <Button
                as="span"
                colorScheme="dark"
                leftIcon={<Icon as={StoreIcon} w="5" h="5" />}
                isFullWidth
              >
                {translate("stakedTraits:buy")}
              </Button>
            </Link>

            <Link to={`/wardrobe/${tokenId}`}>
              <Button
                as="span"
                colorScheme="dark"
                leftIcon={<Icon as={WardrobeIcon} w="5" h="5" />}
                isFullWidth
              >
                {translate("stakedTraits:swap")}
              </Button>
            </Link>
          </SimpleGrid>
        </Box>
      )}

      {selectedTraits && isUnstakePopupOpen && (
        <UnstakePopup
          isOpen
          originalTraits={metadata!.originalTraits}
          currentTraits={metadata!.traits}
          traitsToUnstake={selectedTraits}
          tokenId={tokenId!}
          furColor={furColor!}
          onClose={setIsUnstakePopupOpen.off}
          onSuccess={handleSuccess}
        />
      )}

      {selectedTraits && isUnstakeSuccessPopupOpen && (
        <TraitsChangedPopup
          isOpen
          title={translate("unstake:success:title")}
          description={translate("unstake:success:description")}
          tokenId={tokenId!}
          currentTraits={metadata!.traits}
          newTraits={traitsAfterUnstake}
          furColor={furColor!}
        />
      )}
    </Box>
  );
};

export default BearStakedTraitsContainer;
